import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import Context from '@s-ui/react-context'
import SpinnerBasic, {spinnerBasicOverlayTypes, spinnerBasicTypes} from '@adv-mt/mt-spinner-basic'

import {useGetTrackingEvents} from '../../hooks/useGetTrackingEvents.js'
import {useUtils} from '../../hooks/useUtils.js'
import {BASE_CLASS, PANEL_FINANCING_VERSION, WITH_INSURANCE, WITHOUT_INSURANCE} from '../config.js'
import {PanelFinancingCtaB2c} from '../cta/PanelFinancingCtaB2c.js'
import {PanelFinancingInfo} from '../detailFinancingInfo/PanelFinancingInfo.js'
import {PanelFinancingInstalmentSection} from '../instalment/PanelFinancingInstalmentSection.js'
import {PanelFinancingLenderInfo} from '../lenderInfo/PanelFinancingLenderInfo.js'

export const PanelFinancingContentB2C = ({ad, isProfessional, financingData, onCalculatorChange, vehicleInfo = {}}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {NO_INSURANCE} = config.get('financing').INSURANCE_IDS

  const [loanCalculations, setLoanCalculations] = useState({data: financingData, isLoading: false, error: ''})
  const [entryValue, setEntryValue] = useState(financingData.entry?.toString() || financingData.minEntry?.toString())
  const [savedEntryValue, setSavedEntryValue] = useState(
    financingData.entry?.toString() || financingData.minEntry?.toString()
  )
  const [selectedTerms, setSelectedTerms] = useState(financingData.terms)
  const [savedTerms, setSavedTerms] = useState(financingData.terms)
  const [entryErrorText, setEntryErrorText] = useState('')
  const [selectedFare, setSelectedFare] = useState(NO_INSURANCE)

  const {trackUpdatedCalculationsEvents, trackLegalContent, trackTermsChangeEvents, trackEntryChangeEvents} =
    useGetTrackingEvents()

  const {getInputBottomText, handleScrollToFinancingTerms} = useUtils()

  const {id: adId} = ad

  const {calculatorB2cData, cashPrice, lender, loanRequestAvailable} = loanCalculations.data || {}
  const hasInsurance = selectedFare !== NO_INSURANCE

  const selectedFareData = {
    ...calculatorB2cData[hasInsurance ? WITH_INSURANCE : WITHOUT_INSURANCE],
    cashPrice,
    loanRequestAvailable
  }

  const updateLoanCalculations = async ({
    adId,
    entry,
    currentFee,
    lender,
    currentTerms,
    currentOptionWithLifeInsurance
  }) => {
    setLoanCalculations({...loanCalculations, isLoading: true})
    const [error, response] = await domain.get('get_loan_calculations_B2C_transactions_use_case').execute({
      adId,
      entry: Number(entry),
      lender,
      terms: currentTerms,
      withLifeInsurance: currentOptionWithLifeInsurance
    })

    if (error) {
      setSelectedTerms(savedTerms)
      setEntryValue(savedEntryValue)
      setLoanCalculations({...loanCalculations, error, isLoading: false})
      return
    }

    const chosenFareResponse = currentOptionWithLifeInsurance ? response.withInsurance : response.withoutInsurance

    const updatedFinancingData = {...loanCalculations.data, ...chosenFareResponse}

    setLoanCalculations({
      data: {...updatedFinancingData, calculatorB2cData: response, entry},
      isLoading: false,
      error: ''
    })
    setSavedTerms(currentTerms)
    setSavedEntryValue(entry)
    trackUpdatedCalculationsEvents(ad)
    onCalculatorChange({financingData: {...updatedFinancingData, calculatorB2cData: response, entry}})
  }

  const handleEntryChange = value => {
    const errorText = getInputBottomText({
      maxEntry: selectedFareData.maxEntry,
      minEntry: selectedFareData.minEntry,
      value
    })

    const hasInputError = errorText !== ''

    if (hasInputError) {
      setEntryErrorText(errorText)
      onCalculatorChange({reset: true})
      setEntryValue(value)
      return
    }

    if (value === entryValue) return
    setLoanCalculations({...loanCalculations, error: '', isLoading: false})

    setEntryValue(value)
    setEntryErrorText('')
    trackEntryChangeEvents(ad, value)
  }

  const handleTermsChange = value => {
    setLoanCalculations({...loanCalculations, error: '', isLoading: false})

    setSelectedTerms(value)
    updateLoanCalculations({
      adId,
      entry: entryValue,
      currentFee: selectedFareData.fee,
      lender,
      currentTerms: value,
      currentOptionWithLifeInsurance: selectedFare !== NO_INSURANCE
    })

    trackTermsChangeEvents(ad, value)
  }

  const handleOnBlurEntry = () => {
    const hasInputError = entryErrorText !== ''
    if (hasInputError) {
      setEntryValue(selectedFareData.minEntry)
      setEntryErrorText('')
    }

    if (selectedFareData?.entry === entryValue || (selectedFareData?.entry === undefined && entryValue === '0')) return

    updateLoanCalculations({
      adId,
      entry: hasInputError ? selectedFareData.minEntry : entryValue,
      currentFee: selectedFareData.fee,
      lender,
      currentTerms: selectedTerms,
      currentOptionWithLifeInsurance: selectedFare !== NO_INSURANCE
    })
  }

  const handleOnClickCtaTerms = () => {
    trackLegalContent(ad)
    handleScrollToFinancingTerms()
  }

  const handleOnChangeSelectedFare = value => {
    setSelectedFare(value)
    updateLoanCalculations({
      adId,
      entry: entryValue,
      currentFee: selectedFareData.fee,
      lender,
      currentTerms: selectedTerms,
      currentOptionWithLifeInsurance: value !== NO_INSURANCE
    })
  }

  const hasInputErrors = entryErrorText !== ''
  return (
    <>
      <div className={`${BASE_CLASS}-content`}>
        {loanCalculations.isLoading && (
          <SpinnerBasic
            inheritColorText
            overlayType={spinnerBasicOverlayTypes.LIGHT}
            type={spinnerBasicTypes.SECTION}
            text={i18n.t('COMMON.PROCESS_CAN_TAKE_SECONDS')}
          />
        )}
        <PanelFinancingInstalmentSection
          isProfessional={isProfessional}
          entryErrorText={entryErrorText}
          entryValue={entryValue}
          handleEntryChange={handleEntryChange}
          handleOnBlurEntry={handleOnBlurEntry}
          handleOnChangeSelectedFare={handleOnChangeSelectedFare}
          handleTermsChange={handleTermsChange}
          hasInputError={hasInputErrors}
          loanCalculationsError={loanCalculations.error}
          loanCalculations={loanCalculations.data}
          selectedTerms={selectedTerms}
          selectedFare={selectedFare}
          selectedFareData={selectedFareData}
        />
        <PanelFinancingInfo
          ad={ad}
          isPrivate={!isProfessional}
          loanCalculations={selectedFareData}
          panelFinancingVersion={PANEL_FINANCING_VERSION.B2C}
        />
      </div>
      <PanelFinancingLenderInfo
        ad={ad}
        handleOnClickCtaTerms={handleOnClickCtaTerms}
        isPrivate={!isProfessional}
        loanCalculations={selectedFareData}
      />
      <div className={`${BASE_CLASS}-footerB2c`}>
        <PanelFinancingCtaB2c
          ad={ad}
          financingData={financingData}
          isDisabled={hasInputErrors || loanCalculations.isLoading}
          selectedLoanData={{
            fee: selectedFareData.fee,
            entryValue,
            selectedTerms,
            fareHasInsurance: selectedFare !== NO_INSURANCE
          }}
          loanCalculations={loanCalculations.data}
        />
      </div>
    </>
  )
}

PanelFinancingContentB2C.propTypes = {
  ad: PropTypes.object,
  isProfessional: PropTypes.string,
  financingData: PropTypes.object,
  onCalculatorChange: PropTypes.func,
  vehicleInfo: PropTypes.object
}
